/**
 * Auth context contract (CONTRACTS.md §2). Consumed via useAuth().
 * Role always comes from the server-side profile row — never from
 * localStorage, URL params or form input.
 */
import { createContext } from 'react'
import type { Profile, Role } from '@/types'

export type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated'

/** Roles that can sign in through the public auth pages. */
export type PortalRole = 'client' | 'caregiver'

export type SignInInput = {
  email: string
  password: string
  role: PortalRole
}

export type SignUpInput = {
  email: string
  password: string
  displayName: string
  role: PortalRole
}

export type AuthContextValue = {
  status: AuthStatus
  userId: string | null
  email: string | null
  profile: Profile | null
  role: Role | null
  signIn(input: SignInInput): Promise<void>
  signUp(input: SignUpInput): Promise<{ needsEmailConfirmation: boolean }>
  signOut(): Promise<void>
  sendPasswordReset(email: string): Promise<void>
  updatePassword(newPassword: string): Promise<void>
  refreshProfile(): Promise<void>
}

export const AuthContext = createContext<AuthContextValue | null>(null)
